<template>
  <Listbox as="div" class="relative" :model-value="selectedTags" by="id" multiple @update:model-value="onSelectTag">
    <ListboxButton
      tabindex="-1"
      @keydown.stop.space="() => onToggle()"
      @keydown.stop.enter="() => onToggle()"
      @click.stop.prevent="() => onToggle()"
    >
      <slot name="button-content" />
    </ListboxButton>
    <ListboxOptions
      v-show="open"
      static
      class="absolute -left-36 bottom-10 z-10 mt-1.5 overflow-hidden rounded-md border border-b-0 border-neutral-80 shadow-md focus:outline-none"
      @keydown.stop.escape="() => onToggle()"
    >
      <TagsOptions
        ref="TagsSelectorMenuRef"
        :open="open"
        :selected-tags="selectedTags"
        :tags="tags"
        :allow-create="false"
        class="min-w-[20rem] max-w-[20rem] divide-y bg-white"
        @toggle="onToggle"
      />
    </ListboxOptions>
    <div v-show="open" class="absolute bottom-8 left-0 z-10 inline-block w-16 overflow-hidden">
      <div class="h-5 w-5 origin-top-left -rotate-45 transform bg-white shadow-md"></div>
    </div>
  </Listbox>
</template>

<script lang="ts" setup>
import { ref } from "vue";

// Components
import { Listbox, ListboxButton, ListboxOptions } from "@headlessui/vue";
import TagsOptions from "@organisms/Tags/TagsDropDown/components/TagsOptions.vue";

// Utils
import { onClickOutside } from "@vueuse/core";

// Types
import type { Tags, Tag } from "@/vue/types/tag";

// Props
interface Props {
  selectedTags: Tags;
  tags: Tags;
  open: boolean;
  loading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  selectedTags: () => [],
  tags: () => [],
  open: false,
  loading: false,
});

// Emits
const emit = defineEmits<{
  (e: "toggleTag", tag: Tag): void;
  (e: "open"): void;
  (e: "close"): void;
}>();

const TagsSelectorMenuRef = ref();
onClickOutside(TagsSelectorMenuRef, () => {
  emit("close");
});

const onToggle = () => {
  if (props.open) {
    emit("close");
    return;
  }
  emit("open");
};

const onSelectTag = (values: Tag[]) => {
  if (!values) return;

  const currentSelected = props.selectedTags;
  const newSelected = values;

  let changedTag: Tag | null = null;

  const addedTag = newSelected.find((tag) => !currentSelected.some((current) => current.id === tag.id));
  if (addedTag) {
    changedTag = addedTag;
  } else {
    const removedTag = currentSelected.find((current) => !newSelected.some((tag) => tag.id === current.id));
    if (removedTag) {
      changedTag = removedTag;
    }
  }

  if (changedTag) {
    emit("toggleTag", changedTag);
  }
};
</script>
